import analytics from "./index";

declare global {
  interface Window {
    pixbyAnalytics?: typeof analytics;
  }
}

interface ScriptDataset {
  projectKey: string;
  apiUrl?: string;
  disablePresence?: boolean;
  disableAutocapture?: boolean;
  autocaptureOutboundLinks?: boolean;
  autocaptureDownloads?: boolean;
  autocaptureForms?: boolean;
  autocaptureErrors?: boolean;
  autocaptureClicks?: boolean;
}

type WebVitalMetric = "lcp" | "inp" | "cls" | "fcp" | "ttfb";
type WebVitalRating = "good" | "needs_improvement" | "poor";

interface AutocaptureSettings {
  outboundLinks: boolean;
  downloads: boolean;
  forms: boolean;
  errors: boolean;
  clicks: boolean;
}

interface SDKConfigResponse {
  autocaptureSettings?: Partial<AutocaptureSettings>;
}

interface PendingMetric {
  value: number;
  timer?: number;
  source: string;
}

const DEFAULT_AUTOCAPTURE_SETTINGS: AutocaptureSettings = {
  outboundLinks: true,
  downloads: true,
  forms: true,
  errors: true,
  clicks: false
};

const DOWNLOAD_EXTENSIONS = /\.(7z|csv|doc|docx|gz|ics|jpeg|jpg|pdf|png|ppt|pptx|rar|tar|txt|xls|xlsx|xml|zip)(?:[?#].*)?$/i;

let previousLocation = "";
let historyPatched = false;
let webVitalsBound = false;
let autocaptureBound = false;
let activeAutocaptureSettings = DEFAULT_AUTOCAPTURE_SETTINGS;
const pendingMetrics = new Map<WebVitalMetric, PendingMetric>();
const reportedMetrics = new Set<WebVitalMetric>();

function currentUrl() {
  if (typeof location === "undefined") {
    return "";
  }

  return `${location.pathname}${location.search}${location.hash}`;
}

function currentReferrer() {
  if (typeof document === "undefined" || !document.referrer) {
    return "direct";
  }

  try {
    const url = new URL(document.referrer);
    return url.hostname.replace(/^www\./, "") || "direct";
  } catch {
    return "direct";
  }
}

function currentUTMParams() {
  if (typeof location === "undefined") {
    return {};
  }

  const params = new URLSearchParams(location.search);
  const values: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const value = params.get(key);
    if (value) {
      values[key] = value;
    }
  }

  return values;
}

function trackPageView(force = false) {
  const nextLocation = currentUrl();
  if (!force && (!nextLocation || nextLocation === previousLocation)) {
    return;
  }

  previousLocation = nextLocation;
  const utmParams = currentUTMParams();
  analytics.track("page_viewed", {
    path: typeof location !== "undefined" ? location.pathname : undefined,
    url: nextLocation,
    title: typeof document !== "undefined" ? document.title : undefined,
    referrer: currentReferrer(),
    ...utmParams
  }, {
    referrer: currentReferrer(),
    ...utmParams
  });
}

function bindPageTracking() {
  if (typeof window === "undefined" || historyPatched) {
    return;
  }

  historyPatched = true;
  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = function pushState(...args) {
    originalPushState(...args);
    trackPageView();
  };

  window.history.replaceState = function replaceState(...args) {
    originalReplaceState(...args);
    trackPageView();
  };

  window.addEventListener("popstate", () => {
    trackPageView();
  });

  window.addEventListener("hashchange", () => {
    trackPageView();
  });
}

function bindWebVitalsTracking() {
  if (typeof window === "undefined" || typeof performance === "undefined" || webVitalsBound) {
    return;
  }

  webVitalsBound = true;
  trackTTFB();
  observeFCP();
  observeLCP();
  observeCLS();
  observeINP();

  const flushPending = () => {
    pendingMetrics.forEach((_, metric) => flushWebVital(metric));
    void analytics.flush();
  };

  window.addEventListener("pagehide", flushPending, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPending();
    }
  });
}

function observeFCP() {
  observePerformance("paint", (entries) => {
    const entry = entries.find((item) => item.name === "first-contentful-paint");
    if (entry) {
      reportWebVital("fcp", entry.startTime, "paint");
    }
  });
}

function observeLCP() {
  let latest = 0;

  observePerformance("largest-contentful-paint", (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) {
      return;
    }

    latest = entry.startTime;
    scheduleWebVital("lcp", latest, "largest-contentful-paint");
  });
}

function observeCLS() {
  let value = 0;

  observePerformance("layout-shift", (entries) => {
    for (const entry of entries) {
      const layoutShift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
      if (!layoutShift.hadRecentInput && typeof layoutShift.value === "number") {
        value += layoutShift.value;
      }
    }

    if (value > 0) {
      scheduleWebVital("cls", value, "layout-shift");
    }
  });
}

function observeINP() {
  let value = 0;

  observePerformance(
    "event",
    (entries) => {
      for (const entry of entries) {
        if (entry.duration > value) {
          value = entry.duration;
        }
      }

      if (value > 0) {
        scheduleWebVital("inp", value, "event-timing");
      }
    },
    { durationThreshold: 40 }
  );

  observePerformance("first-input", (entries) => {
    const entry = entries[0] as PerformanceEntry & { processingStart?: number };
    if (!entry || value > 0 || typeof entry.processingStart !== "number") {
      return;
    }

    scheduleWebVital("inp", Math.max(entry.processingStart - entry.startTime, 0), "first-input-fallback");
  });
}

function trackTTFB() {
  const send = () => {
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (!navigation) {
      return;
    }

    const value = navigation.responseStart > 0 ? navigation.responseStart - navigation.requestStart : 0;
    if (value > 0) {
      reportWebVital("ttfb", value, "navigation");
    }
  };

  if (document.readyState === "complete") {
    send();
    return;
  }

  window.addEventListener("load", send, { once: true, passive: true });
}

function observePerformance(
  type: string,
  callback: (entries: PerformanceEntry[]) => void,
  options: Record<string, unknown> = {}
) {
  if (typeof PerformanceObserver === "undefined" || !PerformanceObserver.supportedEntryTypes?.includes(type)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      callback(list.getEntries());
    });
    observer.observe({ type, buffered: true, ...options } as PerformanceObserverInit);
  } catch {
    // Browsers vary in PerformanceObserver support. Unsupported metrics are skipped.
  }
}

function scheduleWebVital(metric: WebVitalMetric, value: number, source: string) {
  if (!Number.isFinite(value) || value < 0 || reportedMetrics.has(metric)) {
    return;
  }

  const current = pendingMetrics.get(metric);
  if (current?.timer) {
    window.clearTimeout(current.timer);
  }

  const timer = window.setTimeout(() => flushWebVital(metric), 3000);
  pendingMetrics.set(metric, { value, timer, source });
}

function flushWebVital(metric: WebVitalMetric) {
  const pending = pendingMetrics.get(metric);
  if (!pending) {
    return;
  }

  pendingMetrics.delete(metric);
  reportWebVital(metric, pending.value, pending.source);
}

function reportWebVital(metric: WebVitalMetric, value: number, source: string) {
  if (!Number.isFinite(value) || value < 0 || reportedMetrics.has(metric)) {
    return;
  }

  reportedMetrics.add(metric);
  const normalizedValue = metric === "cls" ? Math.round(value * 1000) / 1000 : Math.round(value);
  const rating = rateWebVital(metric, normalizedValue);

  analytics.track(
    "web_vital_measured",
    {
      metric,
      value: normalizedValue,
      rating,
      unit: metric === "cls" ? "score" : "ms",
      source,
      path: typeof location !== "undefined" ? location.pathname : undefined,
      url: currentUrl(),
      title: typeof document !== "undefined" ? document.title : undefined
    },
    {
      path: typeof location !== "undefined" ? location.pathname : undefined,
      url: currentUrl(),
      metric,
      rating
    }
  );
}

function rateWebVital(metric: WebVitalMetric, value: number): WebVitalRating {
  const thresholds: Record<WebVitalMetric, [number, number]> = {
    lcp: [2500, 4000],
    inp: [200, 500],
    cls: [0.1, 0.25],
    fcp: [1800, 3000],
    ttfb: [800, 1800]
  };
  const [good, needsImprovement] = thresholds[metric];

  if (value <= good) {
    return "good";
  }
  if (value <= needsImprovement) {
    return "needs_improvement";
  }
  return "poor";
}

async function configureAutocapture(dataset: ScriptDataset) {
  const settings = await resolveAutocaptureSettings(dataset);
  if (Object.values(settings).some(Boolean)) {
    bindAutocaptureTracking(settings);
  }
}

async function resolveAutocaptureSettings(dataset: ScriptDataset): Promise<AutocaptureSettings> {
  if (dataset.disableAutocapture) {
    return {
      outboundLinks: false,
      downloads: false,
      forms: false,
      errors: false,
      clicks: false
    };
  }

  const remoteSettings = await fetchSDKConfig(dataset);
  return {
    outboundLinks: dataset.autocaptureOutboundLinks ?? remoteSettings.outboundLinks,
    downloads: dataset.autocaptureDownloads ?? remoteSettings.downloads,
    forms: dataset.autocaptureForms ?? remoteSettings.forms,
    errors: dataset.autocaptureErrors ?? remoteSettings.errors,
    clicks: dataset.autocaptureClicks ?? remoteSettings.clicks
  };
}

async function fetchSDKConfig(dataset: ScriptDataset): Promise<AutocaptureSettings> {
  const apiUrl = dataset.apiUrl;
  if (!apiUrl || typeof fetch === "undefined") {
    return DEFAULT_AUTOCAPTURE_SETTINGS;
  }

  try {
    const response = await fetch(`${apiUrl.replace(/\/$/, "")}/sdk/config`, {
      method: "GET",
      headers: {
        "X-Project-Key": dataset.projectKey
      },
      cache: "no-store"
    });

    if (!response.ok) {
      return DEFAULT_AUTOCAPTURE_SETTINGS;
    }

    const payload = (await response.json()) as SDKConfigResponse;
    return normalizeAutocaptureSettings(payload.autocaptureSettings);
  } catch {
    return DEFAULT_AUTOCAPTURE_SETTINGS;
  }
}

function normalizeAutocaptureSettings(settings: Partial<AutocaptureSettings> | undefined): AutocaptureSettings {
  return {
    outboundLinks: typeof settings?.outboundLinks === "boolean" ? settings.outboundLinks : DEFAULT_AUTOCAPTURE_SETTINGS.outboundLinks,
    downloads: typeof settings?.downloads === "boolean" ? settings.downloads : DEFAULT_AUTOCAPTURE_SETTINGS.downloads,
    forms: typeof settings?.forms === "boolean" ? settings.forms : DEFAULT_AUTOCAPTURE_SETTINGS.forms,
    errors: typeof settings?.errors === "boolean" ? settings.errors : DEFAULT_AUTOCAPTURE_SETTINGS.errors,
    clicks: typeof settings?.clicks === "boolean" ? settings.clicks : DEFAULT_AUTOCAPTURE_SETTINGS.clicks
  };
}

function bindAutocaptureTracking(settings: AutocaptureSettings) {
  activeAutocaptureSettings = settings;
  if (typeof window === "undefined" || typeof document === "undefined" || autocaptureBound) {
    return;
  }

  autocaptureBound = true;
  document.addEventListener("click", handleAutocaptureClick, true);
  document.addEventListener("submit", handleAutocaptureSubmit, true);
  window.addEventListener("error", handleAutocaptureError);
  window.addEventListener("unhandledrejection", handleAutocaptureRejection);
}

function handleAutocaptureClick(event: MouseEvent) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (anchor && activeAutocaptureSettings.downloads && isDownloadLink(anchor)) {
    const url = safeURL(anchor.href);
    analytics.track("file_downloaded", {
      href: url?.href ?? anchor.href,
      filename: fileNameFromAnchor(anchor, url),
      extension: fileExtensionFromAnchor(anchor, url),
      text: readableText(anchor),
      path: typeof location !== "undefined" ? location.pathname : undefined
    });
  }

  if (anchor && activeAutocaptureSettings.outboundLinks && isOutboundLink(anchor)) {
    const url = safeURL(anchor.href);
    analytics.track("outbound_link_clicked", {
      href: url?.href ?? anchor.href,
      host: url?.hostname ?? "",
      text: readableText(anchor),
      path: typeof location !== "undefined" ? location.pathname : undefined
    });
  }

  if (activeAutocaptureSettings.clicks) {
    const marked = target.closest<HTMLElement>("[data-pixby-capture]");
    if (marked) {
      analytics.track("element_clicked", {
        label: marked.dataset.pixbyCapture || readableText(marked) || marked.id || marked.tagName.toLowerCase(),
        tag: marked.tagName.toLowerCase(),
        id: marked.id || undefined,
        path: typeof location !== "undefined" ? location.pathname : undefined,
        text: readableText(marked)
      });
    }
  }
}

function handleAutocaptureSubmit(event: SubmitEvent) {
  if (!activeAutocaptureSettings.forms) {
    return;
  }

  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const action = form.getAttribute("action") || "";
  const actionUrl = action ? safeURL(action) : null;
  analytics.track("form_submitted", {
    formId: form.id || undefined,
    formName: form.getAttribute("name") || undefined,
    action: actionUrl?.pathname || action || undefined,
    method: (form.getAttribute("method") || "get").toLowerCase(),
    path: typeof location !== "undefined" ? location.pathname : undefined
  });
}

function handleAutocaptureError(event: ErrorEvent) {
  if (!activeAutocaptureSettings.errors) {
    return;
  }

  analytics.track("javascript_error", {
    message: truncate(event.message || "Script error", 240),
    source: event.filename || undefined,
    line: event.lineno || undefined,
    column: event.colno || undefined,
    path: typeof location !== "undefined" ? location.pathname : undefined
  });
}

function handleAutocaptureRejection(event: PromiseRejectionEvent) {
  if (!activeAutocaptureSettings.errors) {
    return;
  }

  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "Unhandled promise rejection";
  analytics.track("javascript_error", {
    message: truncate(message, 240),
    source: "unhandledrejection",
    path: typeof location !== "undefined" ? location.pathname : undefined
  });
}

function isOutboundLink(anchor: HTMLAnchorElement) {
  const url = safeURL(anchor.href);
  return Boolean(url && /^https?:$/.test(url.protocol) && typeof location !== "undefined" && url.origin !== location.origin);
}

function isDownloadLink(anchor: HTMLAnchorElement) {
  if (anchor.hasAttribute("download")) {
    return true;
  }

  const url = safeURL(anchor.href);
  return Boolean(url && DOWNLOAD_EXTENSIONS.test(url.pathname));
}

function fileNameFromAnchor(anchor: HTMLAnchorElement, url: URL | null) {
  const downloadName = anchor.getAttribute("download");
  if (downloadName) {
    return downloadName;
  }

  const path = url?.pathname ?? "";
  const name = path.split("/").filter(Boolean).pop() ?? "";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

function fileExtensionFromAnchor(anchor: HTMLAnchorElement, url: URL | null) {
  const name = fileNameFromAnchor(anchor, url);
  const match = name.match(/\.([a-z0-9]+)$/i);
  return match?.[1]?.toLowerCase() ?? "";
}

function safeURL(value: string) {
  try {
    return new URL(value, typeof location !== "undefined" ? location.href : undefined);
  } catch {
    return null;
  }
}

function readableText(element: Element) {
  const ariaLabel = element.getAttribute("aria-label");
  const title = element.getAttribute("title");
  const text = ariaLabel || title || element.textContent || "";
  return truncate(text.replace(/\s+/g, " ").trim(), 120);
}

function truncate(value: string, maxLength: number) {
  return value.length > maxLength ? `${value.slice(0, Math.max(maxLength - 3, 0))}...` : value;
}

function readOptionalBoolean(value: string | undefined) {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return undefined;
}

function readDataset(): ScriptDataset | null {
  if (typeof document === "undefined") {
    return null;
  }

  const current = document.currentScript;
  const script =
    current instanceof HTMLScriptElement
      ? current
      : document.querySelector<HTMLScriptElement>("script[data-project-key][data-api-url], script[data-project-key]");

  if (!script?.dataset.projectKey) {
    return null;
  }

  return {
    projectKey: script.dataset.projectKey,
    apiUrl: script.dataset.apiUrl,
    disablePresence: script.dataset.disablePresence === "true",
    disableAutocapture: script.dataset.disableAutocapture === "true",
    autocaptureOutboundLinks: readOptionalBoolean(script.dataset.autocaptureOutboundLinks),
    autocaptureDownloads: readOptionalBoolean(script.dataset.autocaptureDownloads),
    autocaptureForms: readOptionalBoolean(script.dataset.autocaptureForms),
    autocaptureErrors: readOptionalBoolean(script.dataset.autocaptureErrors),
    autocaptureClicks: readOptionalBoolean(script.dataset.autocaptureClicks)
  };
}

function boot() {
  const dataset = readDataset();
  if (!dataset?.projectKey) {
    return;
  }

  analytics.init(dataset.projectKey, {
    apiUrl: dataset.apiUrl,
    disablePresence: dataset.disablePresence
  });

  if (typeof window !== "undefined") {
    window.pixbyAnalytics = analytics;
  }

  bindPageTracking();
  bindWebVitalsTracking();
  void configureAutocapture(dataset);
  trackPageView(true);
}

boot();

export default analytics;
